'use client';

import { useState } from 'react';
import { NumberInput } from '~/components/primitives';
import { DEFAULT_DISPLAY_PARTICIPANTS } from '@funkey/constants';
import { withoutDefaultParticipants } from '~/features/activities/search/shared';
import { type ActivityFilterConfig, ActivityFilterBar_Mobile_NumberFilter } from './shared';

/** A filter to select a specific number of people */
export const NUM_PEOPLE_FILTER_CONFIG: ActivityFilterConfig<{
    participants?: number | null;
}> = {
    key: 'minParticipants',
    label: i18n => i18n._('Number of people'),
    placeholder: i18n => i18n._('Select amount'),
    selectionDisplay: (_i18n, value) => value.participants?.toString(),
    footNote: i18n =>
        i18n._(
            'The effective number of participants can be changed up to 14 days before the teambuilding day.',
        ),

    PopupContent(props) {
        return (
            <NumberInput
                id="people"
                name="people"
                allowUndefined
                autoFocus
                className="mb-0!"
                onChange={people =>
                    props.onChange(prev => ({
                        ...prev,
                        participants: people,
                    }))
                }
                min={0}
                // max={100}
                value={props.value.participants ?? undefined}
                step={1}
            />
        );
    },

    mobile: {
        icon: 'people-users',
        ctaText: i18n => i18n._('Choose amount'),
        Component(props) {
            return (
                <ActivityFilterBar_Mobile_NumberFilter
                    value={props.value.participants ?? null}
                    onChange={value =>
                        props.onChange(prev => ({
                            ...prev,
                            participants: value === null ? null : Math.round(value),
                        }))
                    }
                    placeholder={props.i18n._('Choose amount')}
                />
            );
        },
    },
};

/** The same filter with the default group size visible. The reworked PLP prices
 * every card at 20 when nothing is set, so the filter says so instead of
 * showing a placeholder. The default is never written to the URL: typing 20
 * clears the parameter, so URL-based usage numbers stay comparable with the
 * period before the change. */
export const NUM_PEOPLE_DEFAULT_FILTER_CONFIG: typeof NUM_PEOPLE_FILTER_CONFIG = {
    ...NUM_PEOPLE_FILTER_CONFIG,
    // Short enough to fit the bar's button without being cut off.
    label: i18n => i18n._('Participants'),
    selectionDisplay: (_i18n, value) =>
        (value.participants ?? DEFAULT_DISPLAY_PARTICIPANTS).toString(),

    PopupContent(props) {
        // The field keeps its own draft: a cleared field has to stay empty while
        // the visitor types, which a value derived from the filter cannot do because
        // "nothing set" displays as the default.
        const [draft, setDraft] = useState<number | undefined>(
            props.value.participants ?? DEFAULT_DISPLAY_PARTICIPANTS,
        );

        return (
            <NumberInput
                id="people"
                name="people"
                autoFocus
                className="mb-0!"
                onChange={people => {
                    setDraft(people);
                    props.onChange(prev => ({
                        ...prev,
                        participants: withoutDefaultParticipants(people),
                    }));
                }}
                min={1}
                value={draft}
                step={1}
            />
        );
    },

    mobile: {
        ...NUM_PEOPLE_FILTER_CONFIG.mobile,
        Component(props) {
            return (
                <ActivityFilterBar_Mobile_NumberFilter
                    value={props.value.participants ?? DEFAULT_DISPLAY_PARTICIPANTS}
                    onChange={value =>
                        props.onChange(prev => ({
                            ...prev,
                            participants: withoutDefaultParticipants(
                                value === null ? null : Math.round(value),
                            ),
                        }))
                    }
                    // The field opens on 20 rather than on a placeholder, and a
                    // bare number in a filter bar says nothing about what it counts.
                    suffix={props.i18n._('activity.filter.participants.suffix', undefined, {
                        message: 'participants',
                    })}
                    placeholder={props.i18n._('Choose amount')}
                />
            );
        },
    },
};
